import React from 'react';
import {
  Facebook,
  Linkedin,
  Youtube,
  ArrowRight,
  Phone,
  Mail,
  Clock,
  MapPin
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import oldLogo from '../img/torchbearer-logo.png';

const spaces = [
  { name: 'Torchbearer Main', href: 'https://torchbearer.co.ke' },
  { name: 'Installer', href: 'https://installer.torchbearer.co.ke' },
  { name: 'eMobility', href: 'https://emobility.torchbearer.co.ke' },
  { name: 'Greatstar Energy', href: 'https://greatstar.torchbearer.co.ke' },
];

const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/training/about' },
    { name: 'EPRA Licensing', href: '/training/epra' },
    { name: 'Solar Design', href: '/training/solar' },
    { name: 'Electrical Design', href: '/training/electrical' },
    { name: 'Branding', href: '/training/branding' },
    { name: 'Contact', href: '/training/contact' }
  ];

  const trainingPrograms = [
    { name: 'Green Hydrogen', href: '/training/green-hydrogen' },
    { name: 'Electric Vehicle', href: '/training/electric-vehicles' },
    { name: 'Solar PV System', href: '/training/solar' },
    { name: 'Electrical Engineering', href: '/training/electrical' },
    { name: 'OSHA Training', href: '/training/safety-training' }
  ];

  return (
    <footer className="bg-[#f4ebdc] text-gray-900 pt-16 pb-8 md:pt-24 md:pb-12 overflow-hidden border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 mb-16">

          {/* Brand Column */}
          <div className="flex flex-col space-y-6">
            <Link to="/" className="flex items-center gap-3">
              <img src={oldLogo} alt="Torchbearer Logo" className="w-14 h-14 object-contain" />
              <div className="flex flex-col">
                <span className="text-lg font-black text-[#D97706] tracking-tight leading-tight">
                  Torchbearer
                </span>
                <span className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                  Institute of Technologies
                </span>
              </div>
            </Link>

            <p className="text-gray-700 text-sm leading-relaxed max-w-xs">
              Professional training and development for electrical engineering, renewable energy, and career advancement across Africa.
            </p>

            <div className="flex items-center gap-3">
              {[
                { icon: <Facebook size={18} />, href: "https://www.facebook.com/profile.php?id=61572958352380" },
                { icon: <Linkedin size={18} />, href: "https://www.linkedin.com/company/torchbearer-institute-of-technologies" },
                { icon: <FaTiktok size={18} />, href: "https://www.tiktok.com/@torchbearersinstitute" },
                { icon: <Youtube size={18} />, href: "https://www.youtube.com/@TORCHBEARERINSITTUTE" }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center text-gray-600 hover:text-white hover:bg-[#D97706] hover:border-[#D97706] transition-all duration-300"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gray-900 text-sm font-black uppercase tracking-[0.2em] mb-8 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#D97706] rounded-full"></span>
            </h4>
            <ul className="space-y-4">
              {quickLinks.slice(0, 5).map((link, i) => (
                <li key={i}>
                  <Link to={link.href} className="text-gray-600 hover:text-black text-sm flex items-center group transition-colors">
                    <ArrowRight size={14} className="mr-3 text-[#D97706] opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Expertise */}
          <div>
            <h4 className="text-gray-900 text-sm font-black uppercase tracking-[0.2em] mb-8 relative inline-block">
              Expertise
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#D97706] rounded-full"></span>
            </h4>
            <ul className="space-y-4">
              {trainingPrograms.map((program, i) => (
                <li key={i}>
                  <Link to={program.href} className="text-gray-600 hover:text-black text-sm flex items-center group transition-colors">
                    <ArrowRight size={14} className="mr-3 text-[#D97706] opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0" />
                    <span>{program.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-gray-900 text-sm font-black uppercase tracking-[0.2em] mb-8 relative inline-block">
              Contact Info
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#D97706] rounded-full"></span>
            </h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center text-[#D97706] flex-shrink-0">
                  <Phone size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-0.5">Call Us</p>
                  <p className="text-sm text-gray-800 font-medium">+254 789 173033</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center text-[#D97706] flex-shrink-0">
                  <Mail size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-0.5">Email</p>
                  <p className="text-sm text-gray-800 font-medium break-all">training@torchbearer.co.ke</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center text-[#D97706] flex-shrink-0">
                  <MapPin size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-0.5">Location</p>
                  <p className="text-sm text-gray-800 font-medium">Kiambu, Kenya</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-black/5 border border-black/10 flex items-center justify-center text-[#D97706] flex-shrink-0">
                  <Clock size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-0.5">Hours</p>
                  <p className="text-sm text-gray-800 font-medium">Mon - Fri: 8am - 5pm</p>
                </div>
              </li>
            </ul>
          </div>

        </div>

        {/* Our Spaces + Location - Full width row */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-16 pt-10" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div className="flex-shrink-0">
            <h4 className="text-gray-900 text-sm font-black uppercase tracking-[0.2em] mb-6 relative inline-block">
              Our Spaces
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#D97706] rounded-full"></span>
            </h4>
            <ul className="space-y-3">
              {spaces.map((space, i) => (
                <li key={i}>
                  <a
                    href={space.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-black text-sm flex items-center group transition-colors"
                  >
                    <ArrowRight size={14} className="mr-3 text-[#D97706] opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0" />
                    <span>{space.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-gray-900 text-sm font-black uppercase tracking-[0.2em] mb-6 relative inline-block">
              Location
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#D97706] rounded-full"></span>
            </h4>
            <div className="overflow-hidden border border-black/10 rounded-xl">
              <iframe
                title="Torchbearer location map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.948281358941!2d36.8218421!3d-1.162143!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f3dd37ed17fc9%3A0xf13f5dc26681d!2sTORCHBEARER+TECHNOLOGIES!5e0!3m2!1sen!2ske!4v1700000000000"
                width="100%"
                height="180"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-black/10 to-transparent mb-12"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start space-y-1">
            <p className="text-gray-600 text-xs font-medium">
              &copy; {new Date().getFullYear()} Torchbearer Institute of Technologies. All rights reserved.
            </p>
            <p className="text-[10px] text-gray-500 italic">
              Empower the mind energize the community
            </p>
          </div>

          <div className="flex items-center gap-8">
            <Link to="/privacy" className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-[#D97706] transition-colors">Privacy</Link>
            <Link to="/terms" className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-[#D97706] transition-colors">Terms</Link>
            <a
              href="https://www.garikon.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-black/5 border border-black/10 rounded-lg text-[10px] font-black uppercase tracking-widest text-gray-600 hover:text-black hover:bg-black/10 transition-all"
            >
              Crafted by <span className="text-[#D97706]">Garikon</span>
            </a>
          </div>
        </div>

      </div>

    </footer>
  );
};

export default Footer;
